import * as React from 'react';
import DocumentHeader from '../../../components/DocumentHeader';
import DocumentWrapper from '../../../components/DocumentWrapper';
import { FormContextProvider, HistoryContextProvider } from '../../../containers/FormContextHandler';
import FormEditingHandler from '../../../containers/FormEditingHandler';
import {
  docType,
  getHeaderId,
  mapPatientDoesNotWantRespiratorySupportTherapyEndDates,
  patientHasRespiratoryFailureDiagnosis,
  patientHasSleepApneaDiagnosis,
} from '../utils';
import Form from './Form';

import PatientDoesNotWantRespiratorySupportTherapyHistory from './HistoryRowData/PatientDoesNotWantRespiratorySupportTherapy';
import OxygenTherapyHistory from './HistoryRowData/OxygenTherapy';
import HFNCTherapyHistory from './HistoryRowData/HFNCTherapy';
import PAPTherapyHistory from './HistoryRowData/PAPTherapy';
import MADTherapyHistory from './HistoryRowData/MADTherapy';
import SurgicalTreatmentHistory from './HistoryRowData/SurgicalTreatment';
import OtherTreatmentHistory from './HistoryRowData/OtherTreatment';
import { actions, TCreationPromiseType } from '../../../store/documents/actions';
import { assertCapabilities, useAppDispatch, useAppSelector } from 'Store/index';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import Icon from 'Components/_NewElements/Icon';
import { fm } from 'Components/FormatMessage';
import { ICapabilityContextProps, withCapabilities } from 'Containers/CapabilityHandler';
import Dialog, { IThemedDialog } from 'Components/_NewElements/Dialog';
import { TDocument } from './definitions';

export const PAPTherapyContext = React.createContext<IPAPTherapyContext>({
  resMedIntegrationAvailable: false,
  resMedDialogOpen: false,
  setResMedDialogOpen: () => '',
  openInformationDialog: () => '',
});

interface IPAPTherapyContext {
  resMedIntegrationAvailable: boolean;
  resMedDialogOpen: boolean;
  setResMedDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  openInformationDialog: (text: string | React.JSX.Element, hasTimeout?: boolean | number) => void;
}

const TreatmentHistory = ({ documents }: { documents: IOwnProps['documents'] }): React.JSX.Element => {
  const diagnosisDocs: Array<IDiagnosis> = documents.filter(d => d._type === 'diagnosis');

  const noRelevantDiagnosis =
    !patientHasRespiratoryFailureDiagnosis(diagnosisDocs) && !patientHasSleepApneaDiagnosis(diagnosisDocs);
  return (
    <React.Fragment>
      {/** Potilas ei halua hengitystukihoitoa */}
      <PatientDoesNotWantRespiratorySupportTherapyHistory />

      {/** Happihoito */}
      <OxygenTherapyHistory />

      {/** HFNC-hoito */}
      <HFNCTherapyHistory />

      {/** PAP-hoito */}
      <PAPTherapyHistory />

      {/** Uniapneakiskohoito */}
      {(patientHasSleepApneaDiagnosis(diagnosisDocs) || noRelevantDiagnosis) && <MADTherapyHistory />}

      {/** Kirurginen hoito */}
      {(patientHasSleepApneaDiagnosis(diagnosisDocs) || noRelevantDiagnosis) && <SurgicalTreatmentHistory />}

      {/** Muu hoito */}
      <OtherTreatmentHistory />
    </React.Fragment>
  );
};

const Treatment = ({ documents, capabilityGroups = {} }: IOwnProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const createCommit = async (id: string): Promise<TCreationPromiseType> =>
    actions.createCommit({ id, name: 'patientDoesNotWantRespiratorySupportTherapy' })(dispatch);

  const closeCommitWithData = (id: string, data: any & IControlProps) =>
    actions.closeCommit({ id, name: 'patientDoesNotWantRespiratorySupportTherapy' }, data)(dispatch);

  const isEditing = documents.some(d => d._editing === true);

  React.useEffect(() => {
    const docsWithUpdatedEndDates = mapPatientDoesNotWantRespiratorySupportTherapyEndDates(documents);
    // PDNWRST endDates are only updated if they don't exist, not if there isn't a matching treatment start/decision date
    if (!isEditing) {
      docsWithUpdatedEndDates?.forEach(d => {
        createCommit(d._id)
          .then(() => {
            closeCommitWithData(d._id, d);
          })
          .catch(() => {
            return;
          });
      });
    }
  }, [isEditing]);

  const visitReasonRetro = useAppSelector((s: IState) => s.session.data?.visitreason === 'retrospectiveDataTransfer');

  const [informationDialogSettings, setInformationDialogSettings] = React.useState<IThemedDialog | null>(null);

  const openInformationDialog = (text: string | React.JSX.Element, hasTimeout: boolean | number = true) => {
    setInformationDialogSettings({
      open: true,
      children: text,
      onClose: () => setInformationDialogSettings(null),
      closeTimeout: typeof hasTimeout === 'number' ? hasTimeout : hasTimeout ? 3000 : undefined,
    });
  };

  const resMedIntegrationAvailable = assertCapabilities(['RESMED_AVX_INTEGRATION'], capabilityGroups);
  const resMedIntegrationEnabled = useAppSelector((s: IState) => {
    const details = s.session.patientIntegrationDetails;
    if (details === undefined) return undefined;
    return details.resMed !== null;
  });
  const [resMedDialogOpen, setResMedDialogOpen] = React.useState<boolean>(false);

  const resMedContext = {
    resMedIntegrationAvailable,
    resMedDialogOpen,
    setResMedDialogOpen,
    openInformationDialog,
  };

  return (
    <FormEditingHandler name="treatment" documents={documents}>
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <DocumentWrapper>
          <DocumentHeader
            name="treatment"
            headerId={getHeaderId(documents, editing, view?.viewing)}
            editing={editing}
            editButtons={<div />}
            infoText={
              resMedIntegrationAvailable && docType(documents, editing ?? view?.viewing) === 'papTherapy' ? (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: '600' }}>
                    {fm('treatment.papTherapy.resMedIntegration')}{' '}
                    <span style={{ textTransform: 'lowercase' }}>
                      {fm(`treatment.papTherapy.resMed${resMedIntegrationEnabled ? 'InUse' : 'NotInUse'}`)}
                    </span>
                  </div>
                  <ActionButtonRounded
                    text={
                      <Container alignItems="center" justifyContent="space-between">
                        <Item>
                          {resMedIntegrationEnabled ? <Icon icon="mergeType" /> : <Icon icon="sqicon" size={3.1} />}
                        </Item>
                        <Item xs={true}>
                          {fm(`treatment.papTherapy.${resMedIntegrationEnabled ? 'editResMed' : 'connectResMed'}`)}
                        </Item>
                      </Container>
                    }
                    width={26}
                    height={4}
                    fontSize={16}
                    onClick={() => setResMedDialogOpen(true)}
                    disabled={typeof resMedIntegrationEnabled !== 'boolean' || !!view?.viewing}
                  />
                </div>
              ) : undefined
            }
            headerWidth={12}
          />
          <PAPTherapyContext.Provider value={resMedContext}>
            {/** Editing */}
            {editing && documents.find((d: TDocument) => d._id === editing) ? (
              <FormContextProvider context={{ formData, documents, editing }}>
                <Form document={documents.find((d: TDocument) => d._id === editing) as TDocument} />
              </FormContextProvider>
            ) : null}
            {/** Viewing */}
            {view?.viewing && documents.find((d: TDocument) => d._id === view.viewing) ? (
              <FormContextProvider context={{ formData, documents, view }}>
                <Form document={documents.find((d: TDocument) => d._id === view.viewing) as TDocument} />
              </FormContextProvider>
            ) : null}
            {/** History */}
            {!editing && !view?.viewing ? (
              <HistoryContextProvider
                context={{
                  documents: documents,
                  startEdit: startEdit,
                  view: view,
                  additionalData: { visitReasonRetro },
                }}
              >
                <TreatmentHistory documents={documents} />
              </HistoryContextProvider>
            ) : null}
          </PAPTherapyContext.Provider>
          {informationDialogSettings && (
            <Dialog
              open={informationDialogSettings.open}
              type="info"
              onClose={informationDialogSettings.onClose}
              closeTimeout={informationDialogSettings.closeTimeout}
            >
              {informationDialogSettings.children}
            </Dialog>
          )}
        </DocumentWrapper>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps extends ICapabilityContextProps {
  documents: (
    | TDocument
    | IDiagnosis
    | ISleepApneaFirstVisit
    | IRespiratoryFirstVisit
    | ISleepPolygraphy
    | ISleepStudy
  )[];
}

export default withCapabilities(Treatment);
